import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { pricingPlans } from '../mock';
import { Check, Sparkles, CreditCard } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { paymentAPI } from '../services/api';

const PremiumPage = () => {
  const [selectedPlan, setSelectedPlan] = useState('yearly');
  const [processingPayment, setProcessingPayment] = useState(false);
  const { toast } = useToast();

  const handleSubscribe = async (plan, paymentMethod) => {
    const token = localStorage.getItem('token');
    
    if (!token) {
      toast({
        title: 'Login Required',
        description: 'Please login or create an account to subscribe.',
        variant: 'destructive',
      });
      window.location.href = '/login';
      return;
    }
    
    setSelectedPlan(plan.id);
    setProcessingPayment(true);
    
    try {
      toast({
        title: 'Redirecting to checkout...',
        description: `Preparing your ${paymentMethod === 'stripe' ? 'Stripe' : 'PayPal'} payment.`,
      });
      
      const response = await paymentAPI.createSubscription({
        planId: plan.id,
        paymentMethod: paymentMethod
      });
      
      // Redirect to Stripe or PayPal Checkout
      window.location.href = response.checkoutUrl;
      
    } catch (error) {
      toast({
        title: 'Payment Error',
        description: error.response?.data?.detail || 'Could not initiate payment. Please try again.',
        variant: 'destructive',
      });
      setSelectedPlan(null);
      setProcessingPayment(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-green-50 to-white py-12 px-4">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center space-x-2 bg-gradient-to-r from-amber-100 to-yellow-100 px-4 py-2 rounded-full mb-6">
            <Sparkles className="w-4 h-4 text-amber-600" />
            <span className="text-sm font-semibold text-amber-800">Premium Access</span>
          </div>
          
          <h1 className="text-5xl font-bold text-gray-900 mb-4">
            Unlock Your Full Potential
          </h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Get unlimited access to self-coaching tools, AI-powered insights, 
            and personalized guidance for every life domain.
          </p>
        </div>

        {/* Pricing Cards */}
        <div className="grid md:grid-cols-2 gap-8 mb-16">
          {pricingPlans.map((plan) => (
            <Card
              key={plan.id}
              className={`p-8 relative transition-all hover:shadow-2xl ${
                plan.popular
                  ? 'border-4 border-green-500 shadow-xl scale-105'
                  : 'border-2 border-gray-200'
              }`}
            >
              {plan.popular && (
                <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                  <Badge className="bg-gradient-to-r from-green-600 to-green-700 text-white px-4 py-1 text-sm shadow-lg">
                    Most Popular
                  </Badge>
                </div>
              )}

              <div className="text-center mb-6">
                <h3 className="text-2xl font-bold text-gray-900 mb-2">{plan.name}</h3>
                {plan.savings && (
                  <p className="text-sm text-green-600 font-semibold">{plan.savings}</p>
                )}
              </div>

              <div className="text-center mb-8">
                <div className="flex items-baseline justify-center">
                  <span className="text-5xl font-bold text-gray-900">${plan.price}</span>
                  <span className="text-gray-600 ml-2">/{plan.interval}</span>
                </div>
                {plan.interval === 'year' && (
                  <p className="text-sm text-gray-600 mt-2">That's just $8.08 per month</p>
                )}
              </div>

              <div className="space-y-4 mb-8">
                {plan.features.map((feature, index) => (
                  <div key={index} className="flex items-start space-x-3">
                    <div className="flex-shrink-0 w-5 h-5 bg-green-100 rounded-full flex items-center justify-center mt-0.5">
                      <Check className="w-3 h-3 text-green-600" />
                    </div>
                    <span className="text-gray-700">{feature}</span>
                  </div>
                ))}
              </div>

              <Button
                onClick={() => handleSubscribe(plan, 'stripe')}
                disabled={processingPayment}
                className={`w-full py-6 text-lg shadow-lg hover:shadow-xl transition-all mb-3 ${
                  plan.popular
                    ? 'bg-gradient-to-r from-green-600 to-green-700 text-white hover:from-green-700 hover:to-green-800'
                    : 'bg-gray-900 text-white hover:bg-gray-800'
                }`}
              >
                <CreditCard className="w-5 h-5 mr-2" />
                Subscribe with Card
              </Button>
              
              <Button
                onClick={() => handleSubscribe(plan, 'paypal')}
                disabled={processingPayment}
                variant="outline"
                className="w-full py-6 text-lg border-2 border-blue-600 text-blue-600 hover:bg-blue-50"
              >
                <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M20.067 8.478c.492.88.556 2.014.3 3.327-.74 3.806-3.276 5.12-6.514 5.12h-.5a.805.805 0 00-.794.68l-.04.22-.63 3.993-.028.15a.806.806 0 01-.795.68H7.72a.483.483 0 01-.477-.558L7.418 21h1.518l.95-6.02h1.385c4.678 0 7.75-2.203 8.796-6.502z"/>
                  <path d="M2.062 21.617a.783.783 0 01-.771-.906L3.96 6.364a.957.957 0 01.946-.814h6.347c1.22 0 2.228.24 2.994.714.74.456 1.292 1.16 1.643 2.096.177.471.287.99.329 1.553L16.5 11.5c-.99 5.05-4.548 7.5-10.5 7.5H3.437a.805.805 0 01-.795-.68l-1.5-9.5h3.8L3.963 19.5h2.475c3.562 0 5.977-1.653 6.563-4.5h-3.5l1.437-9.06H4.562L2.83 21.037z"/>
                </svg>
                Subscribe with PayPal
              </Button>
            </Card>
          ))}
        </div>

        {/* Features Showcase */}
        <div className="mb-16">
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-12">
            What You'll Get with Premium
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            <Card className="p-6 text-center border-2 border-green-100 hover:border-green-300 transition-all">
              <div className="w-16 h-16 bg-gradient-to-br from-green-400 to-green-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <Sparkles className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">AI Life Coach</h3>
              <p className="text-gray-600">
                Get personalized guidance and insights powered by advanced AI technology
              </p>
            </Card>

            <Card className="p-6 text-center border-2 border-green-100 hover:border-green-300 transition-all">
              <div className="w-16 h-16 bg-gradient-to-br from-blue-400 to-blue-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <Check className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Coaching Tools</h3>
              <p className="text-gray-600">
                Access our complete library of self-coaching tools and exercises
              </p>
            </Card>

            <Card className="p-6 text-center border-2 border-green-100 hover:border-green-300 transition-all">
              <div className="w-16 h-16 bg-gradient-to-br from-purple-400 to-purple-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <Check className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Progress Tracking</h3>
              <p className="text-gray-600">
                Save unlimited assessments and track your growth over time
              </p>
            </Card>
          </div>
        </div>

        {/* Money Back Guarantee */}
        <Card className="p-8 bg-gradient-to-r from-green-50 to-blue-50 border-2 border-green-200 text-center">
          <h3 className="text-2xl font-bold text-gray-900 mb-3">
            30-Day Money-Back Guarantee
          </h3>
          <p className="text-gray-700 max-w-2xl mx-auto">
            Try Premium risk-free. If you're not completely satisfied within the first 30 days, 
            we'll give you a full refund—no questions asked.
          </p>
        </Card>

        {/* FAQ */}
        <div className="mt-16">
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-8">
            Frequently Asked Questions
          </h2>
          <div className="max-w-3xl mx-auto space-y-4">
            <Card className="p-6">
              <h4 className="font-bold text-gray-900 mb-2">Can I cancel anytime?</h4>
              <p className="text-gray-600">
                Yes! You can cancel your subscription at any time. No long-term commitments.
              </p>
            </Card>
            <Card className="p-6">
              <h4 className="font-bold text-gray-900 mb-2">What payment methods do you accept?</h4>
              <p className="text-gray-600">
                We accept all major credit cards, PayPal, and Stripe payments.
              </p>
            </Card>
            <Card className="p-6">
              <h4 className="font-bold text-gray-900 mb-2">Is my data secure?</h4>
              <p className="text-gray-600">
                Absolutely. We use bank-level encryption to protect all your personal information and assessments.
              </p>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PremiumPage;
